/* ═══════════════════════════ the state itself ═══════════════════════════

   `S` is everything studyHub knows, and this module owns it: its default
   shape, how it is read back off disk, and how it is written.

   Two things are worth knowing before editing.

   **`S` is reassigned by `load()`, and that is fine.** ES module bindings are
   live: an importer holding `S` sees the new object, because it is reading the
   binding rather than a copy. Nothing outside this module may assign to it —
   the checker will say so — which is a stricter guarantee than the file had
   when everything shared one scope.

   **`save()` is debounced by 180ms and does not know what a toast is.** It
   reports a failed write through a handler installed by whoever is in a
   position to show one. A store that reaches for the DOM cannot be tested and
   cannot be reused, and a full disk is precisely the moment the message has to
   be right.

   The term and the week are deliberately absent. They are recomputed from this
   whenever a planning tab draws, so there is no stale plan to reconcile. */

import { KEYS, storage, StorageFullError } from './storage.js';
import { COURSE_COLORS, LEGACY_COURSE_COLORS, DESIGN_DEFAULTS } from '../core/palette.js';
import { uid, clamp } from '../core/time.js';
import { LT_KINDS, LT_HORIZONS, WK_KINDS, wkMode, seriesKind } from '../core/domain.js';

/** Told when a write fails, with a sentence fit to show someone. */
let onSaveError = (/** @type {string} */ _message) => {};

/** @param {(message: string) => void} fn */
export function onSaveFailure(fn) { onSaveError = fn; }

/** Drop one background.
 *
 *  `IMAGES` is exported, so it may only be reassigned in here — an importer
 *  writing to it would be assigning to a read-only binding. Pushing into the
 *  array is fine and stays where it is; replacing it needs this. */
export function removeImage(id) {
  IMAGES = IMAGES.filter((i) => i.id !== id);
  return IMAGES;
}

/** A brand-new term. @returns {StudyState} */
export const DEFAULTS = () => /** @type {any} */ ({
  courses: [
    { id: uid(), name: 'General', color: COURSE_COLORS[0] },
  ],
  tasks: [],
  events: [],
  deadlines: [],
  thoughts: [],
  // board-level wandering tasks: caught before they have an estimate, kept
  // forever so "all time" can show what was thought, not just what survived
  wanderTasks: [],
  bg: { activeId: null, dim: 35, blur: 0, fit: 'cover', shuffle: false },
  clock: { format: '12', seconds: true },
  cal: { id: '', mode: 'WEEK' },
  // the term ahead. `entries` is everything the horizon knows that studyHub's
  // own lists do not; `sources` is the syllabus PDFs on disk (metadata only —
  // the files themselves are far too big for localStorage); `seen` remembers
  // which syllabus-derived dates were already kept or waved away, so a second
  // calculation does not re-offer them. `textFinds` is what the parser pulled
  // out of each block of pasted text, kept because the text itself goes to
  // disk and never comes back — the reading happens once, at the paste.
  longterm: { entries: [], sources: [], weeks: 16, filterCourse: '', seen: { kept: [], dismissed: [] }, textFinds: [] },
  // the week itself. `items` is what a week is made of — the lectures and club
  // meetings that repeat, the psets and posts that come due, the gym that wants
  // three slots and does not care which. `misses` is the honest record of what
  // did not happen, kept because it is the only way the next plan can know that
  // Friday at eight in the morning is a fiction. `moved` holds the alternatives
  // you picked afterwards, keyed by the block they replace.
  weekly: {
    items: [], misses: [], moved: {}, filterCourse: '',
    dayStart: 8 * 60, dayEnd: 22 * 60,
    // screenshots of a timetable, pasted section times — the same intake the
    // horizon has, because a week arrives in the same ad-hoc formats a term does
    sources: [],
  },
  // the things that come back every week. `templates` are definitions, not
  // tasks: the tasks are generated from them a few weeks at a time, because a
  // semester materialised up front is a task list nobody can read. Departures
  // from the pattern — a reading week, a paper moved to the Thursday — live in
  // each template's own `exceptions`, so one odd week never disturbs the series
  // around it.
  series: { templates: [], generatedThrough: null },
  // subsCollapsed holds task ids whose subtask checklist is folded away — a view
  // preference, so it lives here rather than on the task itself
  ui: { tab: 'clock', list: 'active', filterCourse: '', noteColorFilter: '', subsCollapsed: [], boardView: 'today', dayDock: 'open', template: 'paper' },
  // the user's overrides on top of whatever template is on; null = use the template's answer
  design: DESIGN_DEFAULTS(),
  timer: { mode: 'normal', workMin: 25, breakMin: 5, longMin: 15, perLong: 4 },
  // live pomodoro state — persisted so a reload mid-break resumes correctly
  pomo: { phase: 'idle', taskId: null, anchorSec: 0, until: 0, done: 0 },
});

/** Everything studyHub knows. Reassigned by `load()` and by nothing else.
 *  @type {StudyState} */
export let S = DEFAULTS();
/** Backgrounds, kept apart from the rest because they are large.
 *  @type {any[]} */
export let IMAGES = [];   // [{id, name, url}]

export function load() {
  try {
    const raw = storage().read(KEYS.main);
    if (raw) S = Object.assign(DEFAULTS(), JSON.parse(raw));
    // shallow-merge nested objects so new keys survive upgrades
    const d = DEFAULTS();
    for (const k of ['bg', 'clock', 'cal', 'ui', 'timer', 'pomo', 'design', 'longterm', 'weekly', 'series']) S[k] = Object.assign(d[k], S[k] || {});
    for (const k of ['courses', 'tasks', 'events', 'deadlines', 'thoughts', 'wanderTasks']) if (!Array.isArray(S[k])) S[k] = d[k];
    // `sync` held the ids of email-derived suggestions already ruled on. Mail is
    // no longer read, so the bookkeeping goes with it; a state saved before this
    // still carries the key and is simply left to fall away on the next save.
    delete S.sync;

    // the horizon: drop anything that cannot be placed on a week, since every
    // view of it is keyed by date
    S.longterm.seen = Object.assign({ kept: [], dismissed: [] }, S.longterm.seen || {});
    for (const k of ['kept', 'dismissed']) if (!Array.isArray(S.longterm.seen[k])) S.longterm.seen[k] = [];
    /* A find with no source is a row that can never be traced back to the text
       it came from, and the review list would offer it forever. */
    if (!Array.isArray(S.longterm.textFinds)) S.longterm.textFinds = [];
    S.longterm.textFinds = S.longterm.textFinds.filter((f) => f && f.sourceId && Array.isArray(f.items));
    S.longterm.entries = (Array.isArray(S.longterm.entries) ? S.longterm.entries : [])
      .filter((e) => e && typeof e.end === 'number')
      .map((e) => ({
        ...e,
        id: e.id || uid(),
        kind: LT_KINDS.some((k) => k.key === e.kind) ? e.kind : 'deadline',
        title: String(e.title || 'Untitled'),
        start: typeof e.start === 'number' ? e.start : null,
        effortMin: Math.max(0, +e.effortMin || 0),
        protect: !!e.protect,
      }));
    /* Sources predate having a type: everything attached before screenshots
       and pasted text existed was, by definition, a PDF. */
    for (const scope of ['longterm', 'weekly']) {
      if (!Array.isArray(S[scope].sources)) S[scope].sources = [];
      S[scope].sources = S[scope].sources
        .filter((s) => s && s.file)
        .map((s) => ({ ...s, id: s.id || uid(), type: s.type || 'pdf' }));
    }
    if (!LT_HORIZONS.includes(S.longterm.weeks)) S.longterm.weeks = 16;

    // the week: a recurring item is meaningless without the days it falls on,
    // and a one-off without its date, so anything missing both is dropped
    S.weekly.items = (Array.isArray(S.weekly.items) ? S.weekly.items : [])
      .map((it) => ({
        ...it,
        id: it.id || uid(),
        kind: WK_KINDS.some((k) => k.key === it.kind) ? it.kind : 'pset',
        title: String(it.title || 'Untitled'),
        recur: it.recur === 'once' ? 'once' : 'weekly',
        days: (Array.isArray(it.days) ? it.days : []).filter((n) => n >= 0 && n <= 6),
        date: typeof it.date === 'string' ? it.date : null,
        time: /^\d{2}:\d{2}$/.test(it.time || '') ? it.time : null,
        durMin: Math.max(0, +it.durMin || 0),
        estMin: Math.max(0, +it.estMin || 0),
        perWeek: clamp(+it.perWeek || 1, 1, 14),
      }))
      // a habit is the one kind with no day of its own — that is what makes it
      // a habit — so it must not be swept up by the day check
      .filter((it) => wkMode(it.kind) === 'habit' || (it.recur === 'weekly' ? it.days.length : it.date));
    /* the series. A template with no day never fires, which would leave a row
       in the drawer that silently does nothing — so those are dropped. Anything
       else is repaired rather than discarded: a template represents a decision
       made once at the start of term and is worth more than its worst field. */
    S.series.templates = (Array.isArray(S.series.templates) ? S.series.templates : [])
      .map((tpl) => ({
        ...tpl,
        id: tpl.id || uid(),
        kind: seriesKind(tpl.kind).key,
        title: String(tpl.title || 'Untitled'),
        courseId: tpl.courseId || '',
        days: (Array.isArray(tpl.days) ? tpl.days : []).filter((n) => Number.isInteger(n) && n >= 0 && n <= 6),
        time: /^\d{2}:\d{2}$/.test(tpl.time || '') ? tpl.time : '09:00',
        from: typeof tpl.from === 'string' ? tpl.from : null,
        until: typeof tpl.until === 'string' ? tpl.until : null,
        estMin: Math.max(0, +tpl.estMin || 0),
        paused: !!tpl.paused,
        notes: typeof tpl.notes === 'string' ? tpl.notes : '',
        // keyed by the occurrence's own date, which never moves even when the
        // occurrence does — that is what keeps re-dating from duplicating it
        exceptions: (tpl.exceptions && typeof tpl.exceptions === 'object') ? tpl.exceptions : {},
      }))
      .filter((tpl) => tpl.days.length);

    /* An unfinished instance whose template is gone refers to nothing, so the
       reference goes and it carries on as an ordinary task. A finished one
       keeps it: the kind and the attributes hanging off it are what the rates
       are mined from, and history does not stop being true when the series
       that produced it is deleted. */
    const tplIds = S.series.templates.map((t) => t.id);
    S.tasks.forEach((t) => {
      if (t.series && !t.done && !tplIds.includes(t.series.templateId)) delete t.series;
    });

    if (!Array.isArray(S.weekly.misses)) S.weekly.misses = [];
    if (!S.weekly.moved || typeof S.weekly.moved !== 'object') S.weekly.moved = {};
    // a day that ends before it starts would make every free-gap empty
    S.weekly.dayStart = clamp(+S.weekly.dayStart || 480, 0, 1380);
    S.weekly.dayEnd = clamp(+S.weekly.dayEnd || 1320, S.weekly.dayStart + 60, 1440);
    if (!Array.isArray(S.ui.subsCollapsed)) S.ui.subsCollapsed = [];
    if (!S.courses.length) S.courses = d.courses;

    S.wanderTasks = S.wanderTasks
      .filter((w) => w && typeof w.text === 'string')
      .map((w) => ({
        ...w,
        state: ['open', 'done', 'moved'].includes(w.state) ? w.state : 'open',
        created: typeof w.created === 'number' ? w.created : Date.now(),
      }));

    // notes gained wandering tasks; older notes have none
    S.thoughts.forEach((t) => {
      t.tasks = (Array.isArray(t.tasks) ? t.tasks : [])
        .filter((w) => w && typeof w.text === 'string' && w.text.trim())
        .map((w) => ({ id: w.id || uid(), text: w.text, done: !!w.done, taskId: w.taskId || null }));
    });

    // a task may point at a deadline; drop links to deadlines that are gone so
    // nothing renders against a missing parent
    S.deadlines = S.deadlines.filter((x) => x && x.id && typeof x.due === 'number');
    const dlIds = S.deadlines.map((x) => x.id);
    S.tasks.forEach((t) => {
      if (!dlIds.includes(t.deadlineId)) t.deadlineId = null;
    });

    // every task carries a checklist; state saved before subtasks existed has
    // none, and a half-written entry must not take the whole list down
    S.tasks.forEach((t) => {
      t.subtasks = (Array.isArray(t.subtasks) ? t.subtasks : [])
        .filter((s) => s && typeof s.title === 'string' && s.title.trim())
        .map((s) => ({ id: s.id || uid(), title: s.title, done: !!s.done }));
    });

    // courses store their colour by value, so state saved under the old dark
    // theme keeps its pale hexes forever unless we remap them once
    S.courses.forEach((c) => {
      const i = LEGACY_COURSE_COLORS.indexOf(String(c.color || '').toLowerCase());
      if (i !== -1) c.color = COURSE_COLORS[i];
    });
  } catch (e) { console.warn('state load failed, starting fresh', e); S = DEFAULTS(); }

  try {
    const raw = storage().read(KEYS.images);
    IMAGES = raw ? JSON.parse(raw) : [];
  } catch (e) { IMAGES = []; }
}

/* The debounce handle. Not exported any more: nothing outside this module has
   any business cancelling a pending save.
   @type {ReturnType<typeof setTimeout> | undefined} */
let saveTimer;
export function save() {
  clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    try { storage().write(KEYS.main, JSON.stringify(S)); }
    catch (e) {
      onSaveError(e instanceof StorageFullError
        ? 'Could not save — browser storage is full. Export a backup, then clear some backgrounds.'
        : 'Could not save.');
    }
  }, 180);
}
export function saveImages() {
  try { storage().write(KEYS.images, JSON.stringify(IMAGES)); return true; }
  catch (e) {
    onSaveError('Image storage is full. Delete a background and try again.');
    return false;
  }
}

/* ─────────────────────────── courses ───────────────────────────
   Queries over S, wanted by nearly every part of the interface. They live with
   the state rather than with any one tab because no tab owns them. */

export const courseById = (id) => S.courses.find((c) => c.id === id) || null;
export const courseName = (id) => (courseById(id) || {}).name || 'No course';
export const courseColor = (id) => (courseById(id) || {}).color || 'var(--line-2)';
